import { useCallback, useEffect, useState } from "react";
import { useDrop, DropTargetMonitor, useDragLayer, DragLayerMonitor } from "react-dnd";
import { IFlight, IFullFlight, IFullPairingPosition } from "@vmosolutions/oc-interfaces";
import { IContentForm, getInitialFullFlights } from "./ReviewContentWindow";
import { Theme, Grid, Typography, Box } from "@mui/material";
import moment from "moment-timezone";
import { FlightChip } from "src/components/Flights/FlightChip";
import { CheckboxField } from "src/components/FormikComponents/CheckboxField";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";

interface IRenderFlightInfoProps {
  flight: IFullFlight;
  searchDepartureDate: number;
  type: string;
}
interface IAssociatedFlightDropTargetProps {
  formProps: IContentForm;
  searchDepartureDate: number;
  setFirstFlightWithCrew: any;
}

export type ICrewOnFlight = IFullPairingPosition & { pairingId: string; pairingShort: string };
export interface IFlightWithCrew extends Partial<IFullFlight> {
  crew: ICrewOnFlight[];
}
/**
 * When supplied a full flight object, returns a rendered row containing stylized info depending on customer needs.
 * TODO Add customer style/label optons through props.
 * @param flight - IFullFlight object.
 * @param searchDepartureDate - original search date.
 * @param type - available_flight | associated_flight
 * @returns
 */
export const RenderFlightInfo = (props: IRenderFlightInfoProps): JSX.Element => {
  const { flight, searchDepartureDate, type } = props;
  const searchDay = moment(searchDepartureDate).utc().format("MMM Do");
  const flightDepartureDay = moment(flight.actualOff).utc().format("MMM Do");
  const flightArrivalDay = moment(flight.actualOn).utc().format("MMM Do");
  return (
    <Grid container className="available_flight" key={`${type}_${flight?._id}`}>
      <FlightChip data={flight} disableFocus={true} />
      <Typography variant="body2" color="textPrimary" style={{ margin: "5px 0px 5px 15px" }}>
        UP
      </Typography>
      <Typography variant="body2" style={{ margin: 5 }}>
        {searchDay !== flightDepartureDay && `${flightDepartureDay}, `}
        {moment(flight.actualOff).utc().format("HHmm")}
      </Typography>
      <Typography variant="body2" color="textPrimary" style={{ margin: "5px 0px 5px 15px" }}>
        DOWN
      </Typography>
      <Typography variant="body2" style={{ margin: 5 }}>
        {searchDay !== flightArrivalDay && `${flightArrivalDay}, `}
        {moment(flight.actualOn).utc().format("HHmm")}
      </Typography>
    </Grid>
  );
};

export const AssociatedFlightDropTarget = (props: IAssociatedFlightDropTargetProps): JSX.Element => {
  // Set pending position flight.
  const { formProps, searchDepartureDate, setFirstFlightWithCrew } = props;
  const associatedFlights = (formProps?.values?.associatedFlights as IFullFlight[]) ?? [];
  const sortedFlights = associatedFlights.sort((a: IFullFlight, b: IFullFlight) =>
    a.actualOut < b.actualOut ? -1 : 1
  );
  const firstFlight = sortedFlights[0];
  const [loadingFlights, setLoadingFlights] = useState<boolean>(false);
  //const firstFlightId = firstFlight?._id;

  // Use the first flight in the "associatedFlights" to retrieve assigned crew.
  useEffect(() => {
    if (firstFlight?._id) {
      const populateCrewOnFlight = async (): Promise<void> => {
        const flightWithCrew: IFlightWithCrew = { ...firstFlight, crew: null };
        const response = await AmplifyAPIWrapper.Get<Record<string, any>>(`/flight/${firstFlight?._id}/crew`);

        if (response?.crew?.length > 0) {
          flightWithCrew.crew = response?.crew.map((positionCrew: any) => {
            return positionCrew.position;
          });
        }
        // console.log("flightWithCrew", flightWithCrew);
        setFirstFlightWithCrew(flightWithCrew);
      };
      populateCrewOnFlight();
    }
  }, [firstFlight, setFirstFlightWithCrew]);

  // Monitor item being dragged, return as valid if type matches accepted.
  const { isValidDraggable } = useDragLayer((monitor: DragLayerMonitor) => {
    const draggedItem: any = monitor.getItem();
    return {
      isValidDraggable:
        monitor.isDragging() && (draggedItem?.type === "FLIGHT" || draggedItem?.type === "SELECTED_FLIGHTS"),
    };
  });

  const removeAssociatedFlight = useCallback(
    (flightId: string) => {
      const { values, setFieldValue, setFieldTouched } = formProps;
      if (flightId && values && setFieldValue && setFieldTouched) {
        const newAssociatedFlights = [...values.associatedFlights].sort((a: any, b: any) =>
          a.actualOut < b.actualOut ? -1 : 1
        );
        const removeIndex = (newAssociatedFlights as IFullFlight[]).findIndex((f) => f._id === flightId);
        if (removeIndex > -1) {
          newAssociatedFlights.splice(removeIndex, 1);
          setFieldValue("associatedFlights", newAssociatedFlights);
          setFieldTouched("associatedFlights", true);
        }
      }
    },
    [formProps]
  );

  // HANDLE FLIGHTS if dragged over eventgroups
  const [{ isOver }, drop] = useDrop({
    accept: ["FLIGHT", "SELECTED_FLIGHTS"],
    drop(item: any, monitor: DropTargetMonitor) {
      let newFlights: IFlight[] = [];
      switch (item.type) {
        case "FLIGHT":
          newFlights = [item.flight];
          break;
        case "SELECTED_FLIGHTS":
          newFlights = item.selectedFlights;
          break;
        default:
        // code block
      }
      const currentFlights = formProps?.values?.associatedFlights;
      const currentFlightIds = currentFlights.map((flight: any) => flight._id);
      // Filter out flights that are already part of this list if re-added
      const uniqueNew = newFlights.filter((flight) => !currentFlightIds.includes(flight._id));

      if (uniqueNew.length > 0) {
        const addNewFlightsToForm = async (): Promise<void> => {
          setLoadingFlights(true);
          const uniqueIds = uniqueNew.map((f) => f._id);
          const newFullFlights = await getInitialFullFlights(uniqueIds);
          // New sorted associatedFlights values.
          const updatedFlights = [...currentFlights, ...newFullFlights].sort((a: any, b: any) =>
            a.actualOut < b.actualOut ? -1 : 1
          );
          formProps.setFieldValue("associatedFlights", updatedFlights);
          formProps.setFieldTouched("associatedFlights", true);
          setLoadingFlights(false);
        };
        addNewFlightsToForm();
      }
    },
    // hover(item: any, monitor: DropTargetMonitor) {
    //   let newFlights: IFlight[] = [];
    //   switch (item.type) {
    //     case "SELECTED_FLIGHTS":
    //       newFlights = item.selectedFlights;
    //       break;
    //     case "FLIGHT":
    //       newFlights = [item.flight];
    //       break;
    //     default:
    //     // code block
    //   }
    // },
    // canDrop(item: any, monitor: DropTargetMonitor) {
    //   return item?.selectedFlights?.length > 0;
    // },
    collect(monitor: DropTargetMonitor) {
      return {
        isOver: monitor.isOver(),
      };
    },
  });

  const DropZoneStyles = (theme: Theme): any => ({
    event_dropzone: {
      display: "flex",
      cursor: "pointer",
      minHeight: "100px",
      justifyContent: "center",
      alignItems: "flex-start",
      flexDirection: "column",
      flexGrow: 1,
      backgroundColor: theme.palette.background.paper,
      borderRadius: "3px",
      padding: "4px",
      "& .title": { fontSize: 18, fontWeight: "bold" },
      "& .body": {
        display: "flex",
        border: `2x solid ${theme.custom.forms.inputBorder}`,
        minHeight: "40px",
        alignItems: "center",
        color: theme.custom.forms.focusedColor,
        fontSize: 12,
        fontWeight: 300,
        textAlign: "center",
        padding: "4px 8px",
        opacity: 0.5,
        userSelect: "none",
      },
      "&.disabled": {
        opacity: 0.5,
        userSelect: "none",
        pointerEvents: "none",
      },
      "&.reject": {
        backgroundcolor: theme.custom.forms.errorColor,
      },
      "&.active": {
        backgroundcolor: theme.custom.forms.focusedColor,
      },
      "&.dirty": {
        backgroundcolor: theme.custom.forms.dirtyColor,
      },
      "&.over": {
        backgroundcolor: theme.custom.forms.dirtyColor,
      },
    },
  });

  return (
    <Box
      className={`dropzone  ${isValidDraggable && "active"} ${isOver && "over"}`}
      ref={drop}
      sx={(theme) => DropZoneStyles(theme).event_dropzone}
    >
      {sortedFlights?.length === 0 ? (
        <div className="body">
          To associate flights to this flight log, drag/drop them into this zone from the search, timeline or advanced
          search menu.
        </div>
      ) : (
        <div className="body">
          If flight properties not available in the logsheet review need to be modified, it can be done using the
          context menu on each flight chip. Once updated, refresh the flights using the button above.
        </div>
      )}

      {loadingFlights && <div className="body">Reloading flights...</div>}
      {sortedFlights.map((flight, index) => (
        <Grid container wrap="nowrap" alignItems="center" key={index}>
          <CheckboxField value={true} onChange={() => removeAssociatedFlight(flight?._id)} />
          <RenderFlightInfo flight={flight} searchDepartureDate={searchDepartureDate} type="associated_flight" />
        </Grid>
      ))}
    </Box>
  );
};
